import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useDocumentMeta } from '@/hooks/useDocumentMeta'

export const Route = createFileRoute('/login')({
  component: Login,
})

function Login() {
  useDocumentMeta({
    title: 'Entrar - AutoWash Pro',
    description: 'Acesse seu painel do AutoWash Pro de forma rápida e segura.',
  })

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`, // Redireciona para raiz para avaliação do auth state
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      if (error) throw error
    } catch (err: any) {
      setError(err.message || 'Erro ao autenticar com o Google')
      setIsLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        throw error
      }

      if (data.session) {
        // Redireciona para o root para avaliar o onboarding
        navigate({ to: '/' })
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegister = async () => {
    setIsLoading(true)
    setError('')
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })
      if (error) throw error
      if (data.user) {
        navigate({ to: '/' })
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao registrar')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 text-center pt-8">
          <div className="flex justify-center mb-1">
            <img src="/logo.webp" alt="AutoWash Pro Logo" className="h-24 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight text-slate-900 pt-3">
            Bem-vindo ao Painel
          </CardTitle>
          <CardDescription className="text-slate-500">
            Acesse ou crie sua conta para gerenciar seu negócio
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md">
                {error}
              </div>
            )}
            <div className="space-y-2 text-left">
              <Label htmlFor="email" className="text-slate-700 font-medium">E-mail corporativo</Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="focus:border-blue-500 focus:ring-blue-500/20"
              />
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="password" className="text-slate-700 font-medium">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="focus:border-blue-500 focus:ring-blue-500/20"
              />
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={isLoading} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-5"
              >
                {isLoading ? 'Entrando...' : 'Entrar com E-mail'}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-slate-500 font-medium tracking-wider">Ou continue com</span>
                </div>
              </div>

              <Button 
                type="button" 
                variant="outline" 
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full relative flex items-center justify-center gap-2 bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium py-5"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 absolute left-4" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </Button>

              <Button 
                type="button" 
                variant="ghost" 
                onClick={handleRegister}
                disabled={isLoading}
                className="w-full text-slate-500 hover:text-blue-600 hover:bg-blue-50 mt-2 font-medium"
              >
                Ainda não tem conta? Criar agora
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
